/**
 * GameRenderer - Canvas rendering for the Path Game
 * Handles all visual rendering and mouse interaction
 */

import type { Point, GameState, PlayerConfig } from './types';
import { GameEngine, distance } from './GameEngine';

/** Rendering configuration */
interface RenderConfig {
  padding: number;
  cellSize: number;
  colors: {
    background: string;
    gridLines: string;
    labels: string;
    player1: string;
    player1Highlight: string;
    player2: string;
    player2Highlight: string;
    hover: string;
    preview: string;
  };
}

/** Get default render config based on grid size */
function getDefaultConfig(gridSize: number): RenderConfig {
  let cellSize = 60;
  if (gridSize <= 5) {
    cellSize = 80;
  } else if (gridSize <= 7) {
    cellSize = 60;
  } else {
    cellSize = 50;
  }

  return {
    padding: 30,
    cellSize,
    colors: {
      background: '#2a2a4a',
      gridLines: '#444466',
      labels: '#666',
      player1: '#e74c3c',
      player1Highlight: '#ff6b5b',
      player2: '#3498db',
      player2Highlight: '#5dade2',
      hover: 'rgba(46, 204, 113, 0.5)',
      preview: 'rgba(46, 204, 113, 0.3)',
    },
  };
}

export class GameRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private engine: GameEngine;
  private config: RenderConfig;

  // Interaction state
  private hoverPoint: Point | null = null;
  private pendingPoint: Point | null = null;
  private animationFrameId: number | null = null;

  // Player config (for determining if current player is computer)
  private playerConfig: { 1: PlayerConfig; 2: PlayerConfig };

  // Callbacks
  private onMoveAttempt: ((from: Point, to: Point, isFirstMove: boolean) => void) | null = null;

  // Bound event handlers (for proper removal)
  private boundHandleClick: (e: MouseEvent) => void;
  private boundHandleMouseMove: (e: MouseEvent) => void;
  private boundHandleMouseLeave: () => void;

  constructor(
    canvas: HTMLCanvasElement,
    engine: GameEngine,
    playerConfig: { 1: PlayerConfig; 2: PlayerConfig }
  ) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas 2d context');
    this.ctx = ctx;
    this.engine = engine;
    this.playerConfig = playerConfig;

    const settings = engine.getSettings();
    this.config = getDefaultConfig(settings.gridSize);

    // Bind event handlers so they can be properly removed later
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleMouseMove = this.handleMouseMove.bind(this);
    this.boundHandleMouseLeave = this.handleMouseLeave.bind(this);

    this.updateCanvasSize();
    this.setupEventListeners();
  }

  /** Update player config (for when game restarts with different settings) */
  updatePlayerConfig(playerConfig: { 1: PlayerConfig; 2: PlayerConfig }): void {
    this.playerConfig = playerConfig;
  }

  /** Set callback for move attempts */
  setMoveCallback(callback: (from: Point, to: Point, isFirstMove: boolean) => void): void {
    this.onMoveAttempt = callback;
  }

  /** Update canvas size based on grid */
  private updateCanvasSize(): void {
    const state = this.engine.getState();
    this.config = getDefaultConfig(state.gridSize);
    const canvasSize = this.config.padding * 2 + (state.gridSize - 1) * this.config.cellSize;
    this.canvas.width = canvasSize;
    this.canvas.height = canvasSize;
  }

  /** Convert grid coordinates to pixel coordinates */
  gridToPixel(gx: number, gy: number): { x: number; y: number } {
    const state = this.engine.getState();
    return {
      x: this.config.padding + gx * this.config.cellSize,
      y: this.config.padding + (state.gridSize - 1 - gy) * this.config.cellSize,
    };
  }

  /** Convert pixel coordinates to grid coordinates */
  pixelToGrid(px: number, py: number): Point | null {
    const state = this.engine.getState();
    const gx = Math.round((px - this.config.padding) / this.config.cellSize);
    const gy = Math.round(
      state.gridSize - 1 - (py - this.config.padding) / this.config.cellSize
    );
    if (gx >= 0 && gx < state.gridSize && gy >= 0 && gy < state.gridSize) {
      return { x: gx, y: gy };
    }
    return null;
  }

  /** Setup mouse event listeners */
  private setupEventListeners(): void {
    this.canvas.addEventListener('click', this.boundHandleClick);
    this.canvas.addEventListener('mousemove', this.boundHandleMouseMove);
    this.canvas.addEventListener('mouseleave', this.boundHandleMouseLeave);
  }

  /** Handle mouse move */
  private handleMouseMove(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const gridPos = this.pixelToGrid(px, py);

    if (gridPos) {
      const pixelPos = this.gridToPixel(gridPos.x, gridPos.y);
      const dist = Math.sqrt(Math.pow(px - pixelPos.x, 2) + Math.pow(py - pixelPos.y, 2));
      if (dist < 20) {
        this.hoverPoint = gridPos;
      } else {
        this.hoverPoint = null;
      }
    } else {
      this.hoverPoint = null;
    }
    this.draw();
  }

  /** Handle mouse leave */
  private handleMouseLeave(): void {
    this.hoverPoint = null;
    this.draw();
  }

  /** Handle click */
  private handleClick(e: MouseEvent): void {
    const state = this.engine.getState();

    if (state.gameOver) return;
    if (this.playerConfig[state.currentPlayer].type === 'computer') return;

    const rect = this.canvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const gridPos = this.pixelToGrid(px, py);

    if (!gridPos) return;

    // Check if clicking near an intersection
    const pixelPos = this.gridToPixel(gridPos.x, gridPos.y);
    const dist = Math.sqrt(Math.pow(px - pixelPos.x, 2) + Math.pow(py - pixelPos.y, 2));
    if (dist > 20) return;

    const player = state.players[state.currentPlayer];

    // Check if point is already occupied
    if (state.allPoints.some((p) => p.x === gridPos.x && p.y === gridPos.y)) {
      this.showError('Point already occupied!');
      return;
    }

    if (this.pendingPoint === null) {
      // First click
      if (player.pos !== null) {
        // Player already has a position - this click is the destination
        if (this.onMoveAttempt) {
          this.onMoveAttempt(player.pos, gridPos, false);
        }
      } else {
        // First move for this player - set pending point
        this.pendingPoint = gridPos;
        this.draw();
      }
    } else {
      // Second click - complete the first move
      if (gridPos.x === this.pendingPoint.x && gridPos.y === this.pendingPoint.y) {
        // Clicked same point - cancel
        this.pendingPoint = null;
        this.draw();
        return;
      }

      // Check if start point got occupied while waiting
      if (state.allPoints.some((p) => p.x === this.pendingPoint!.x && p.y === this.pendingPoint!.y)) {
        this.showError('Start point is now occupied!');
        this.pendingPoint = null;
        return;
      }

      if (this.onMoveAttempt) {
        this.onMoveAttempt(this.pendingPoint, gridPos, true);
      }
      this.pendingPoint = null;
    }
  }

  /** Clear pending point (called after successful move or game reset) */
  clearPendingPoint(): void {
    this.pendingPoint = null;
  }

  /** Show error message */
  showError(msg: string): void {
    const status = document.getElementById('status');
    if (status) {
      status.textContent = '❌ ' + msg;
      status.style.background = 'rgba(231, 76, 60, 0.3)';
      setTimeout(() => {
        status.style.background = 'rgba(255,255,255,0.1)';
      }, 2000);
    }
  }

  /** Main draw function */
  draw(): void {
    const state = this.engine.getState();
    const { ctx } = this;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.drawGrid(state);
    this.drawPaths(state);
    this.drawPoints(state);
    this.drawPendingPoint(state);
    this.drawCurrentPositionIndicators(state);
    this.drawHoverPreview(state);
  }

  /** Draw the grid background and lines */
  private drawGrid(state: GameState): void {
    const { ctx, config } = this;
    const gridSize = state.gridSize;

    // Draw background
    ctx.fillStyle = config.colors.background;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw grid lines
    ctx.strokeStyle = config.colors.gridLines;
    ctx.lineWidth = 1;

    for (let i = 0; i < gridSize; i++) {
      ctx.beginPath();
      ctx.moveTo(config.padding + i * config.cellSize, config.padding);
      ctx.lineTo(
        config.padding + i * config.cellSize,
        config.padding + (gridSize - 1) * config.cellSize
      );
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(config.padding, config.padding + i * config.cellSize);
      ctx.lineTo(
        config.padding + (gridSize - 1) * config.cellSize,
        config.padding + i * config.cellSize
      );
      ctx.stroke();
    }

    // Draw coordinate labels
    ctx.fillStyle = config.colors.labels;
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < gridSize; i++) {
      ctx.fillText(
        String.fromCharCode(65 + i),
        config.padding + i * config.cellSize,
        config.padding + (gridSize - 1) * config.cellSize + 20
      );
      ctx.fillText(
        (gridSize - i).toString(),
        config.padding - 15,
        config.padding + i * config.cellSize
      );
    }
  }

  /** Draw player paths (lines) */
  private drawPaths(state: GameState): void {
    const { ctx, config } = this;

    ctx.lineWidth = 4;
    ctx.lineCap = 'round';

    ctx.strokeStyle = config.colors.player1;
    this.drawPath(state.players[1].path);

    ctx.strokeStyle = config.colors.player2;
    this.drawPath(state.players[2].path);
  }

  /** Draw a single path */
  private drawPath(path: Point[]): void {
    if (path.length < 2) return;

    const { ctx } = this;
    ctx.beginPath();
    const start = this.gridToPixel(path[0].x, path[0].y);
    ctx.moveTo(start.x, start.y);

    for (let i = 1; i < path.length; i++) {
      const point = this.gridToPixel(path[i].x, path[i].y);
      ctx.lineTo(point.x, point.y);
    }
    ctx.stroke();
  }

  /** Draw all points on the board */
  private drawPoints(state: GameState): void {
    const { ctx, config } = this;

    for (const point of state.allPoints) {
      const pos = this.gridToPixel(point.x, point.y);
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 10, 0, Math.PI * 2);
      ctx.fillStyle = point.player === 1 ? config.colors.player1 : config.colors.player2;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  /** Draw pending point with pulsing animation */
  private drawPendingPoint(state: GameState): void {
    if (!this.pendingPoint) return;

    const { ctx } = this;
    const pos = this.gridToPixel(this.pendingPoint.x, this.pendingPoint.y);
    const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 200);

    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 10 + pulse * 4, 0, Math.PI * 2);
    ctx.fillStyle =
      state.currentPlayer === 1
        ? `rgba(231, 76, 60, ${0.5 + pulse * 0.5})`
        : `rgba(52, 152, 219, ${0.5 + pulse * 0.5})`;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Request animation frame for pulsing
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.animationFrameId = requestAnimationFrame(() => this.draw());
  }

  /** Draw current position indicators */
  private drawCurrentPositionIndicators(state: GameState): void {
    const { ctx, config } = this;

    for (let p = 1; p <= 2; p++) {
      const player = state.players[p as 1 | 2];
      if (player.pos) {
        const pos = this.gridToPixel(player.pos.x, player.pos.y);
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 14, 0, Math.PI * 2);
        ctx.strokeStyle = p === 1 ? config.colors.player1Highlight : config.colors.player2Highlight;
        ctx.lineWidth = 3;
        ctx.stroke();
      }
    }
  }

  /** Draw hover indicator and preview line */
  private drawHoverPreview(state: GameState): void {
    if (!this.hoverPoint || state.gameOver) return;

    const { ctx } = this;
    const hp = this.hoverPoint;
    const isOccupied = state.allPoints.some((p) => p.x === hp.x && p.y === hp.y);

    if (!isOccupied) {
      const pos = this.gridToPixel(hp.x, hp.y);
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 12, 0, Math.PI * 2);
      ctx.fillStyle = this.config.colors.hover;
      ctx.fill();

      // Draw preview line
      const player = state.players[state.currentPlayer];
      let fromPoint: Point | null = null;

      if (this.pendingPoint) {
        fromPoint = this.pendingPoint;
      } else if (player.pos) {
        fromPoint = player.pos;
      }

      if (fromPoint && !(fromPoint.x === hp.x && fromPoint.y === hp.y)) {
        const fromPos = this.gridToPixel(fromPoint.x, fromPoint.y);
        ctx.beginPath();
        ctx.moveTo(fromPos.x, fromPos.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.strokeStyle = this.config.colors.preview;
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 5]);
        ctx.stroke();
        ctx.setLineDash([]);

        // Show distance
        const dist = distance(fromPoint, hp);
        const midX = (fromPos.x + pos.x) / 2;
        const midY = (fromPos.y + pos.y) / 2;
        ctx.fillStyle = '#2ecc71';
        ctx.font = 'bold 12px sans-serif';
        ctx.fillText(dist.toFixed(2), midX, midY - 10);
      }
    }
  }

  /** Draw a replay state (for replay functionality) */
  drawReplayState(
    players: { 1: { pos: Point | null; path: Point[] }; 2: { pos: Point | null; path: Point[] } },
    points: Array<{ x: number; y: number; player: 1 | 2 }>,
    highlightMove: { to: Point } | null,
    gridSize: number
  ): void {
    const { ctx, config } = this;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw background
    ctx.fillStyle = config.colors.background;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Draw grid lines
    ctx.strokeStyle = config.colors.gridLines;
    ctx.lineWidth = 1;

    for (let i = 0; i < gridSize; i++) {
      ctx.beginPath();
      ctx.moveTo(config.padding + i * config.cellSize, config.padding);
      ctx.lineTo(
        config.padding + i * config.cellSize,
        config.padding + (gridSize - 1) * config.cellSize
      );
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(config.padding, config.padding + i * config.cellSize);
      ctx.lineTo(
        config.padding + (gridSize - 1) * config.cellSize,
        config.padding + i * config.cellSize
      );
      ctx.stroke();
    }

    // Draw coordinate labels
    ctx.fillStyle = config.colors.labels;
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i < gridSize; i++) {
      ctx.fillText(
        String.fromCharCode(65 + i),
        config.padding + i * config.cellSize,
        config.padding + (gridSize - 1) * config.cellSize + 20
      );
      ctx.fillText(
        (gridSize - i).toString(),
        config.padding - 15,
        config.padding + i * config.cellSize
      );
    }

    // Draw player paths
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';

    ctx.strokeStyle = config.colors.player1;
    this.drawPath(players[1].path);

    ctx.strokeStyle = config.colors.player2;
    this.drawPath(players[2].path);

    // Draw all points
    for (const point of points) {
      const pos = this.gridToPixel(point.x, point.y);
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 10, 0, Math.PI * 2);
      ctx.fillStyle = point.player === 1 ? config.colors.player1 : config.colors.player2;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Highlight the last move
    if (highlightMove) {
      const toPos = this.gridToPixel(highlightMove.to.x, highlightMove.to.y);
      ctx.beginPath();
      ctx.arc(toPos.x, toPos.y, 16, 0, Math.PI * 2);
      ctx.strokeStyle = '#2ecc71';
      ctx.lineWidth = 3;
      ctx.stroke();
    }
  }

  /** Cleanup - removes event listeners and cancels animations */
  destroy(): void {
    // Remove event listeners
    this.canvas.removeEventListener('click', this.boundHandleClick);
    this.canvas.removeEventListener('mousemove', this.boundHandleMouseMove);
    this.canvas.removeEventListener('mouseleave', this.boundHandleMouseLeave);
    
    // Cancel any pending animation
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }
}
